// src/components/Auth/SocialLoginButtons.js
import React, { useState, useEffect } from 'react';
import '../../styles/components/SocialLoginButtons.css';
import { fetchSocialProviders, startSocialLogin, socialErrorMessage } from '../../api/socialAuth';

/**
 * Detects if the current user device is running iOS (iPhone, iPad, iPod).
 * Supports iPadOS 13+ detection (which identifies as MacIntel with touch support).
 * Also supports query param (?os=ios or ?mock_ios=1) to allow testing on desktop.
 */
export const isIOSDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('os') === 'ios' || params.get('mock_ios') === '1' || params.get('mock_ios') === 'true') {
      return true;
    }
  } catch (e) {
    // Ignore URL parsing errors
  }

  const ua = navigator.userAgent || navigator.vendor || window.opera || '';

  // Direct iOS check (iPhone, iPad, iPod)
  const isIOS = /iPad|iPhone|iPod/.test(ua);

  // iPadOS 13+ detection (Desktop Safari on iPad reports as Macintosh Intel, but has touch capability)
  const isIPadOS =
    (navigator.platform === 'MacIntel' || ua.includes('Macintosh')) &&
    navigator.maxTouchPoints > 1 &&
    !window.MSStream;

  return (isIOS || isIPadOS) && !window.MSStream;
};

/**
 * Apple devices — iPhone, iPad and Mac — where "Sign in with Apple" is offered.
 * Macs report "Macintosh" / platform "MacIntel" (Apple Silicon Macs too).
 */
export const isAppleDevice = () => {
  if (isIOSDevice()) return true;
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const platform = navigator.userAgentData?.platform || navigator.platform || '';
  return /Macintosh|Mac OS X/.test(ua) || /^Mac/i.test(platform) || platform === 'macOS';
};

// Official Google Multi-color Logo
const GoogleIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
  </svg>
);

// Official LinkedIn Logo
const LinkedInIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
  </svg>
);

// Official Apple Logo
const AppleIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.62-.75 1.04-1.8 0.92-2.85-.9.04-2 .6-2.65 1.35-.58.66-1.09 1.73-.95 2.76 1.01.08 2.06-.51 2.68-1.26z" />
  </svg>
);

const SOCIAL_PROVIDERS = [
  { key: 'linkedin', label: 'LinkedIn', icon: <LinkedInIcon size={16} /> },
  { key: 'google', label: 'Google', icon: <GoogleIcon size={18} /> },
  { key: 'apple', label: 'Apple', icon: <AppleIcon size={16} />, appleOnly: true }
];

/**
 * "Continue with …" buttons: LinkedIn, Google, and Apple (on iPhone, iPad and Mac). Clicking sends the browser to the provider's own sign-in page to
 * approve; the backend verifies the result and signs the member in (see src/api/socialAuth.js).
 * Never ask for an email here — typing an email proves nothing about who someone is.
 * If a provider isn't set up on the server yet, a short message says so instead.
 */
const SocialLoginButtons = ({ t, showDivider = true }) => {
  const [isApple, setIsApple] = useState(false); // iPhone, iPad or Mac
  const [available, setAvailable] = useState(null); // { google: bool, … }; null while loading
  const [redirecting, setRedirecting] = useState(null);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    setIsApple(isAppleDevice());
    let cancelled = false;
    fetchSocialProviders()
      .then((res) => { if (!cancelled) setAvailable(res || {}); })
      .catch(() => { if (!cancelled) setAvailable({}); });
    return () => { cancelled = true; };
  }, []);

  const handleSocialClick = (provider) => {
    setNotice('');
    if (available && !available[provider.key]) {
      setNotice(socialErrorMessage('not_configured', provider.key, t));
      return;
    }
    setRedirecting(provider.key);
    startSocialLogin(provider.key);
  };

  const visibleButtons = SOCIAL_PROVIDERS.filter((b) => !b.appleOnly || isApple);

  return (
    <div className="social-auth-container">
      <div className={`social-auth-grid ${isApple ? 'has-apple' : ''}`}>
        {visibleButtons.map((b) => (
          <button
            key={b.key}
            type="button"
            id={`social-login-${b.key}`}
            className={`social-btn social-btn-${b.key}`}
            onClick={() => handleSocialClick(b)}
            disabled={!!redirecting}
            title={t ? t(`continue_with_${b.key}`, b.label) : b.label}
            aria-label={`Sign in with ${b.label}`}
          >
            <span className="social-btn-icon">{b.icon}</span>
            <span className="social-btn-text">
              {redirecting === b.key
                ? (t ? t('social_redirecting', 'Redirecting…') : 'Redirecting…')
                : (t ? t(`continue_with_${b.key}`, b.label) : b.label)}
            </span>
          </button>
        ))}
      </div>

      {notice && (
        <div className="social-auth-notice" role="status">{notice}</div>
      )}

      {showDivider && (
        <div className="social-auth-divider">
          <span>{t ? t('or_with_credentials', 'OR WITH EMAIL & PASSWORD') : 'OR WITH EMAIL & PASSWORD'}</span>
        </div>
      )}
    </div>
  );
};

export default SocialLoginButtons;
